import React from 'react';
import { format, parseISO } from 'date-fns';
import { adToBS } from '../../utils/bs';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import type { TooltipProps } from 'recharts';
import type { DayForecast } from '../../types';

const HydroOverviewTooltip = ({ active, payload, label }: TooltipProps<number, string>) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-hydro-border rounded-lg px-3 py-2 text-xs space-y-1 shadow-sm">
      <p className="font-semibold text-slate-700">{label}</p>
      {payload.map((p) => (
        <p key={p.name} style={{ color: p.color ?? '#64748b' }}>
          {p.name}: {Number(p.value).toFixed(1)} {p.name === 'Precipitation' ? 'mm' : p.name === 'Generation' ? 'MWh' : ''}
        </p>
      ))}
    </div>
  );
};

interface HydroOverviewChartProps {
  days:  DayForecast[];
  title?: string;
}

export default function HydroOverviewChart({ days, title = '7-Day Hydrological Overview' }: HydroOverviewChartProps) {
  const data = days.map((d) => {
    const adDate = parseISO(d.date);
    const nd     = adToBS(adDate);
    return {
      date:          `${format(adDate, 'EEE')} ${nd.getDate()}`,
      Precipitation: d.total_precip_mm,
      Generation:    Math.round(d.total_generation_kwh / 1000),
    };
  });

  return (
    <div className="card">
      <p className="text-sm font-semibold text-slate-900 mb-4">{title}</p>
      <ResponsiveContainer width="100%" height={220}>
        <ComposedChart data={data} margin={{ top: 4, right: 20, left: -5, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey="date" tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} />
          <YAxis yAxisId="left"  tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} axisLine={false} label={{ value: 'mm', angle: -90, position: 'insideLeft', fill: '#94a3b8', fontSize: 9, dy: 20 }} />
          <YAxis yAxisId="right" orientation="right" tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} axisLine={false} label={{ value: 'MWh', angle: 90, position: 'insideRight', fill: '#94a3b8', fontSize: 9, dy: -20 }} />
          <Tooltip content={<HydroOverviewTooltip />} />
          <Legend wrapperStyle={{ fontSize: 11, color: '#64748b' }} />
          <Line  yAxisId="left"  dataKey="Precipitation" stroke="#3b82f6" strokeWidth={2} dot={false} />
          <Bar   yAxisId="right" dataKey="Generation"    fill="#6ee7b7" fillOpacity={0.7} radius={[2,2,0,0]} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
